import type { ReactElement } from 'react';
import { Fragment, useState, useEffect } from 'react';

import Address from 'models/Address';
import UpcomingMenu from 'models/UpcomingMenu';
import { NotificationType } from 'models/Notifications';
import type { INotification } from 'models/Notifications';
import useNotifications from 'hooks/useNotifications';
import { getQueryParam } from 'utils/location';

import ShipToRecipientModal from '../modals/ShipToRecipientModal';

type Recipient = 'friend' | 'me';
type Props = { item: UpcomingMenu; };

function RecipientDetails({ item }: Props): ReactElement {
  const {
    shipments,
    isModifiable,
    delivery_on: deliveryOn,
    recipient_email: recipientEmail,
    menu_id: menuId,
  } = item;

  const { dispatch } = useNotifications();
  const [showShipTo, setShowShipTo] = useState(false);
  const [recipient, setRecipient] = useState<Recipient>(
    recipientEmail ? 'friend' : 'me',
  );

  const editMenu = getQueryParam('menu_id') === `${menuId}`;
  const editAddress = getQueryParam('edit_address') === 'true';

  const addressParams = [
    'edit_address=true',
    `menu_id=${menuId}`,
  ];

  const {
    fullName,
    firstLine,
    secondLine,
    lastLine,
  } = new Address(item.shipping_address);

  const addAddressUpdatedNotification = (address, date) => {
    dispatch({
      type: 'addNotification',
      notification: {
        id: address?.id,
        notification_type: NotificationType.ADDRESS_CHANGE,
        date: date || deliveryOn,
        operation: 'UPDATE',
        address,
      } as INotification,
    });
  };

  const handleAddressEditing = (modalState) => {
    window.history.pushState(
      null,
      // @ts-expect-error TODO: fix null safety
      null,
      `${window.location.pathname}${modalState ? `?${addressParams.join('&')}` : ''}`,
    );

    setShowShipTo(modalState);
  };

  useEffect(() => {
    setShowShipTo(editMenu && editAddress);
  }, [editMenu, editAddress]);

  return (
    <>
      <div className="col-12 col-sm-7 col-lg-6">
        <div className="row">
          <div className="col-12 col-lg-6 c-order-details__recipient-info">
            <span className="c-heading c-heading--brand-8">
              RECIPIENT
            </span>

            <div className="u-text u-text--md">
              {fullName}
              {recipientEmail && (
                <>
                  <br />
                  {recipientEmail}
                </>
              )}
            </div>

          </div>
          <div className="col-12 col-lg-6">
            <div>
              <span className="c-heading c-heading--brand-8">
                DELIVERY ADDRESS
              </span>
            </div>

            <div className="u-text u-text--md">
              {firstLine}
              <br />
              {secondLine}
              {secondLine && <br />}
              {lastLine}
              <br />
              {isModifiable && (
                <button
                  type="button"
                  className="c-link u-text--md"
                  onClick={() => handleAddressEditing(true)}
                >
                  Edit Address
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ShipToRecipientModal
        show={showShipTo}
        recipient={recipient}
        onRecipientChange={setRecipient}
        onAddressUpdate={addAddressUpdatedNotification}
        onShow={() => setShowShipTo(true)}
        onClose={() => handleAddressEditing(false)}
      />
    </>
  );
}

export default RecipientDetails;
